import { useTRPC, useTRPCClient } from "#trpc";
import { TextDisplayWidget } from "#components/display";
import { humanizeNumericValue } from "@dmca/utils/functions";
import type {
  IGHGResource,
  IGHGResourceData,
} from "@dmca/models/interface/ghg";
import { DateTime } from "@dmca/utils/luxon";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import GasMeterIcon from "@mui/icons-material/GasMeter";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Component, Suspense, lazy, type ReactNode } from "react";
import { projectedMonthlyUsage } from "./measurementAvailability";
import { formatMeasurementUnit } from "./formatMeasurementUnit";

const GHGUsageLineGraph = lazy(() => import("./GHGUsageLineGraph"));

class ChartErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  override state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override render() {
    return this.state.hasError ? (
      <Typography color="error" variant="body2">
        Failed to load chart — try refreshing
      </Typography>
    ) : (
      this.props.children
    );
  }
}

function resourceName(resource: IGHGResource) {
  return (
    resource.friendlyName ??
    resource.id.replace(/^./, (letter) => letter.toUpperCase())
  );
}

function ResourceIcon({ resourceId }: { resourceId: string }) {
  if (resourceId === "electrical") return <ElectricBoltIcon color="warning" />;
  if (resourceId === "water") return <WaterDropIcon color="info" />;
  if (resourceId === "gas") return <GasMeterIcon color="action" />;
  return <LocalFireDepartmentIcon color="warning" />;
}

export function PowerUsageMonthlySection({
  resources,
  lastReport,
  timeRange,
  showPulseMeters,
}: {
  resources: IGHGResource[];
  lastReport?: Record<string, IGHGResourceData>;
  timeRange: { start: number | DateTime; end: number | DateTime };
  showPulseMeters?: boolean;
}) {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();
  const sortedResources = resources
    .slice()
    .sort(
      (a, b) =>
        ["electrical", "water", "gas", "lpg"].indexOf(a.id) -
        ["electrical", "water", "gas", "lpg"].indexOf(b.id)
    );

  const resourceIds = sortedResources.map((resource) => resource.id);
  const metersQuery = useQuery({
    ...trpc.pulseMeter.getMultiple.queryOptions(),
    enabled: !!showPulseMeters,
  });
  const pulseMeters = metersQuery.data;
  const pulseMeterIds = pulseMeters?.map((meter) => meter.id) ?? [];
  const usageQuery = useQuery({
    queryKey: trpc.pulseMeter.getCurrentUsage.queryKey({ pulseMeterIds }),
    // Query only today's pulses
    queryFn: () => {
      const now = DateTime.now();
      return trpcClient.pulseMeter.getCurrentUsage.query({
        pulseMeterIds,
        start: now.startOf("day"),
        end: now,
      });
    },
    enabled: !!showPulseMeters && pulseMeterIds.length > 0,
    refetchInterval: 5_000,
  });

  return (
    <Stack gap={2} data-testid="power-monthly-usage">
      {sortedResources.map((resource) => {
        const unit = formatMeasurementUnit(resource.unitOfMeasurement);
        const report = lastReport?.[resource.id];
        const name = resourceName(resource);
        const projectedUsage =
          showPulseMeters &&
          !metersQuery.isError &&
          !usageQuery.isError &&
          pulseMeters &&
          usageQuery.data
            ? projectedMonthlyUsage(pulseMeters, usageQuery.data, resource)
            : undefined;

        return (
          <Paper key={resource.id} variant="outlined" sx={{ p: 2 }}>
            <Stack gap={2}>
              <Stack direction="row" alignItems="center" gap={1}>
                <ResourceIcon resourceId={resource.id} />
                <Typography variant="h6">{name}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <TextDisplayWidget
                  label={`${name} (Most Recent Month${
                    report
                      ? `: ${report.reportDate.toUTC().toFormat("LLLL yyyy")}`
                      : ""
                  })`}
                  helpText="Usage from the most recent submitted monthly report for this resource."
                  sx={{ flex: "1 1 200px" }}
                >
                  {report
                    ? humanizeNumericValue(report.value, unit, {
                        precision: 0,
                        displaySeparator: true,
                        maxMagnitude: 0,
                      })
                    : "Unavailable"}
                </TextDisplayWidget>
                <TextDisplayWidget
                  label={`${name} (Projected Monthly)`}
                  helpText="Estimated monthly usage if the latest measurable pulse rate continued for the whole month."
                  sx={{ flex: "1 1 200px" }}
                >
                  {projectedUsage !== undefined
                    ? humanizeNumericValue(projectedUsage, unit, {
                        precision: 0,
                        displaySeparator: true,
                        maxMagnitude: 0,
                      })
                    : "Unavailable"}
                </TextDisplayWidget>
              </Stack>
              <Box
                sx={{ height: "30vh", minHeight: 180 }}
                data-testid={`power-monthly-${resource.id}-graph`}
              >
                <ChartErrorBoundary>
                  <Suspense fallback={<Typography>Loading Chart</Typography>}>
                    <GHGUsageLineGraph
                      resourceId={resource.id}
                      unit={unit}
                      queryScope={resourceIds}
                      timeRange={timeRange}
                    />
                  </Suspense>
                </ChartErrorBoundary>
              </Box>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
